import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import CreateAttributeModal from '@/modules/Administration/components/settings/CreateAttributeModal';
import { ATTRIBUTE_ICONS } from '@/modules/CRM/components/CRMRecordSidebar';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — PageSettings
 * ═══════════════════════════════════════════════════════════════════════════
 */

const PANEL_VARIANTS = {
    initial: { opacity: 0, y: 6, scale: 0.97 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit:    { opacity: 0, y: 4, scale: 0.97 },
};
const PANEL_TRANSITION   = { duration: 0.15, ease: [0.16, 1, 0.3, 1] };
const REDUCED_TRANSITION = { duration: 0.01 };  // respects prefers-reduced-motion

export function PageSettings({
    isOpen,
    onClose,
    columns,
    hiddenColumns,
    onToggleColumn,
    onReorderColumns,
    recordType,
    pageId
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [savedViews, setSavedViews] = useState([]);
    const prefersReducedMotion = useReducedMotion();

    React.useEffect(() => {
        if (!pageId) return;
        const stored = localStorage.getItem(`oblique_savedViews_${pageId}`);
        if (stored) {
            try { setSavedViews(JSON.parse(stored)); } catch (e) {}
        }
    }, [pageId, isOpen]);

    const handleDeleteView = (viewId) => {
        const updatedViews = savedViews.filter(v => v.id !== viewId);
        setSavedViews(updatedViews);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updatedViews));
    };

    const filteredColumns = useMemo(() => {
        if (!searchQuery) return columns;
        return columns.filter(col => col.label.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [columns, searchQuery]);

    const moveColumn = (index, direction) => {
        if (!onReorderColumns) return;
        const newCols = [...columns];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newCols.length) return;
        
        // Swap
        const temp = newCols[index];
        newCols[index] = newCols[targetIndex];
        newCols[targetIndex] = temp;
        
        onReorderColumns(newCols);
    };

    return (
        <React.Fragment>
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <div 
                            className="fixed inset-0 z-[1999] bg-transparent" 
                            onClick={onClose} 
                        />
                        <motion.div
                            variants={PANEL_VARIANTS}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={prefersReducedMotion ? REDUCED_TRANSITION : PANEL_TRANSITION}
                            className="absolute top-[calc(100%+8px)] right-0 w-[280px] bg-[var(--bg-surface)] rounded-[10px] border border-[var(--border)] shadow-[0_12px_36px_rgba(0,0,0,0.15)] z-[2000] flex flex-col overflow-hidden"
                        >
                            {/* Minimal Search Header */}
                            <div className="p-2 border-b border-[var(--border-subtle)] flex items-center gap-2 bg-[var(--bg-surface)]">
                                <ObliqueIcon name="search" size={14} color="var(--text-tertiary)" />
                                <input
                                    type="text"
                                    placeholder="Search properties..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="border-none bg-transparent text-[12px] text-[var(--text-primary)] outline-none w-full shadow-none p-0"
                                />
                            </div>

                            {/* Column List */}
                            <div className="max-h-[260px] overflow-y-auto py-1.5 px-0 custom-scrollbar">
                                {filteredColumns.map((col, idx) => {
                                    const isHidden = hiddenColumns.includes(col.id);
                                    return (
                                        <div key={col.id} className="flex items-center justify-between py-1.5 px-3 rounded-md transition-colors duration-100 hover:bg-[var(--bg-subtle)]">
                                            <div className="flex items-center gap-2">
                                                {/* Reorder arrows */}
                                                <div className="flex flex-col gap-[1px]">
                                                    <button
                                                        type="button"
                                                        disabled={idx === 0}
                                                        onClick={() => moveColumn(idx, -1)}
                                                        className={`border-none bg-transparent p-0 leading-none h-[10px] text-[var(--text-primary)] ${idx === 0 ? 'cursor-default opacity-20' : 'cursor-pointer opacity-60 hover:opacity-100'}`}
                                                    >
                                                        <ObliqueIcon name="keyboard_arrow_up" size={12} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={idx === columns.length - 1}
                                                        onClick={() => moveColumn(idx, 1)}
                                                        className={`border-none bg-transparent p-0 leading-none h-[10px] text-[var(--text-primary)] ${idx === columns.length - 1 ? 'cursor-default opacity-20' : 'cursor-pointer opacity-60 hover:opacity-100'}`}
                                                    >
                                                        <ObliqueIcon name="keyboard_arrow_down" size={12} />
                                                    </button>
                                                </div>

                                                <ObliqueIcon
                                                    name={ATTRIBUTE_ICONS[col.type] || col.icon || 'view_column'}
                                                    size={14}
                                                    color="var(--text-secondary)"
                                                />
                                                <span className={`text-[12.5px] font-medium ${isHidden ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-primary)]'}`}>{col.label}</span>
                                            </div>

                                            <button
                                                onClick={() => onToggleColumn(col.id)}
                                                className="border-none bg-transparent cursor-pointer flex items-center justify-center p-1 rounded hover:bg-[var(--bg-muted)] transition-colors"
                                            >
                                                <ObliqueIcon
                                                    name={isHidden ? "visibility_off" : "visibility"}
                                                    size={16}
                                                    color={isHidden ? "var(--text-tertiary)" : "var(--accent)"}
                                                />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Saved Views Management */}
                            {pageId && savedViews.length > 0 && (
                                <>
                                    <div className="py-1 px-2 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider bg-[var(--bg-surface)]">
                                        Manage Views
                                    </div>
                                    <div className="max-h-[140px] overflow-y-auto py-1 px-1 custom-scrollbar">
                                        {savedViews.map(view => (
                                            <div key={view.id} className="flex items-center justify-between py-1.5 px-2 rounded-md transition-colors duration-100 hover:bg-[var(--bg-subtle)]">
                                                <div className="flex items-center gap-2 overflow-hidden">
                                                    <ObliqueIcon name="star" size={14} color="var(--text-secondary)" />
                                                    <span className="text-[12.5px] font-medium text-[var(--text-primary)] overflow-hidden text-ellipsis whitespace-nowrap">{view.name}</span>
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteView(view.id)}
                                                    className="border-none bg-transparent cursor-pointer flex items-center justify-center p-1 rounded opacity-50 hover:opacity-100 hover:bg-[var(--danger-subtle)] hover:text-[var(--danger)] transition-all"
                                                    title="Delete view"
                                                >
                                                    <ObliqueIcon name="delete" size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* Action Footer */}
                            <div className="border-t border-[var(--border-subtle)] p-1 bg-[var(--bg-surface)]">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(true)}
                                    className="w-full flex items-center gap-1.5 py-1.5 px-2 border-none bg-transparent rounded-md text-[12px] font-medium text-[var(--text-secondary)] cursor-pointer text-left transition-colors duration-150 hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)]"
                                >
                                    <ObliqueIcon name="add" size={15} color="var(--text-secondary)" />
                                    <span>Create new property</span>
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Create Attribute Modal */}
            <AnimatePresence>
                {showCreateModal && (
                    <CreateAttributeModal
                        isOpen={showCreateModal}
                        onClose={() => setShowCreateModal(false)}
                        recordType={recordType}
                    />
                )}
            </AnimatePresence>
        </React.Fragment>
    );
}
